import {useMachine} from '@xstate/react';
import {useEffect, useMemo} from 'react';

import {assertAndContinue} from 'Asserts';
import {StudyDesignType} from 'graphql-client/__generated/graphqlGlobalTypes';
import NavStore from 'nav/NavStore';
import {getInitialRegistry} from 'registry/DefaultRegistry';
import {usePlateMapFromGrid} from 'samples/grids/hooks/usePlateMapFromGrid';
import {
  PlateDefinitionType,
  PlateDesignState,
  PlateMapType,
} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import {PlateDataParameter, PlateMap} from 'samples/plate-maps/PlateMapConstants';
import {shouldShowTestOrderDilutionInfo} from 'samples/plate-maps/PlateMapUtil';
import {
  filterValidation,
  getErrorsByLayerAndLevel,
  getFlattenedValidation,
} from 'samples/plate-maps/validation/PlateMapValidationUtil';
import {PlateMapErrorLevel, PlateMapValidation} from 'samples/plate-maps/validation/ValidationTypes';

import {AnnotateFillPlateModalMachine} from './machines/AnnotateFillPlateMachine';
import {
  AnnotateFillPlateModalReducerActionType,
  AnnotateFillPlateModalState,
  DEFAULT_ANNOTATE_FILL_PLATE_MODAL_STATE,
  DEFAULT_PLATE_DESIGN_STATE,
} from './machines/AnnotateFillPlateMachineConstants';

export type AnnotateFillPlateModalReducerDispatch = ReturnType<
  typeof useMachine<typeof AnnotateFillPlateModalMachine>
>[1];

/**
 * Handles the modal steps for the annotate and fill plate modal and the state for defining
 * a plate and fetching or creating the corresponding plate map for it.
 *
 * Actions for the annotating and filling steps are handled through a separate reducer shared with
 * the fill plate modal.
 *
 * @param initialState convenience for setting initial state in tests.
 * @param initialStudyIds should not be used in conjunction with initialState.
 * @param isInProcedure whether this modal is being launched from a procedure.
 * @param plateDataParameters if this modal is being launched from a procedure, all data parameters
 *                            associated with said procedure
 */
export default function useAnnotateFillPlateModalReducer({
  initialState,
  initialStudyIds,
  isInProcedure = false,
  plateDataParameters = [],
  initialPlateDesignState = null,
  initialPlateMap = null,
  initialPlateMapType = null,
  initialDismissablePlateMapValidation = null,
  procedureStudyDesignType = null,
}: {
  initialState?: AnnotateFillPlateModalState;
  isInProcedure?: boolean;
  initialStudyIds?: Array<string>;
  plateDataParameters?: Array<PlateDataParameter>;
  initialPlateDesignState?: PlateDesignState | null;
  initialPlateMap?: PlateMap | null;
  initialPlateMapType?: PlateMapType | null;
  initialDismissablePlateMapValidation?: PlateMapValidation | null;
  procedureStudyDesignType?: StudyDesignType | null;
}): [AnnotateFillPlateModalState, AnnotateFillPlateModalReducerDispatch] {
  const initialRegistry = useMemo(() => {
    const allRegistries = NavStore.getRegistries();
    // If we're coming from a plate design, we already have a schema, so we already know the correct
    // registry.
    if (initialPlateDesignState != null && initialPlateDesignState.plateSchema != null) {
      return allRegistries.find(
        (registry) => registry.id === initialPlateDesignState.plateSchema!.registryId
      )!;
    }
    return getInitialRegistry(allRegistries);
  }, [initialPlateDesignState]);
  let plateMapType: PlateMapType | null;
  if (initialPlateMapType == null) {
    if (initialPlateDesignState != null) {
      plateMapType = PlateMapType.DESIGN;
    } else {
      plateMapType = null;
    }
  } else {
    plateMapType = initialPlateMapType;
  }
  // We shouldn't be getting any non-dismissable errors back from plate map validation, but strip them out
  // anyways.
  const initialWarnings: PlateMapValidation =
    initialDismissablePlateMapValidation == null
      ? {
          layerValidation: [],
          wellValidation: [],
          fieldValueValidation: [],
          reagentValidation: [],
        }
      : filterValidation(
          initialDismissablePlateMapValidation,
          (error) => error.level === PlateMapErrorLevel.WARNING
        );
  const initialValidationLength =
    initialDismissablePlateMapValidation != null
      ? getFlattenedValidation(initialDismissablePlateMapValidation).length
      : 0;
  const filteredLength = getFlattenedValidation(initialWarnings).length;
  assertAndContinue(
    initialValidationLength === filteredLength,
    'Unexpected non-dismissable error in staleness validation response'
  );
  const dismissablePlateMapValidation = getErrorsByLayerAndLevel(initialWarnings);
  const defaultState: AnnotateFillPlateModalState = {
    ...DEFAULT_ANNOTATE_FILL_PLATE_MODAL_STATE,
    plateMapType,
    existingPlateState: {
      ...DEFAULT_ANNOTATE_FILL_PLATE_MODAL_STATE.existingPlateState,
      appendStudyIds: initialStudyIds ?? [],
    },
    createPlatePaneState: {
      ...DEFAULT_ANNOTATE_FILL_PLATE_MODAL_STATE.createPlatePaneState,
      studyIds: initialStudyIds ?? [],
      registry: initialRegistry,
      selectedSchema: initialPlateDesignState?.plateSchema ?? null,
    },
    plateDesignState: initialPlateDesignState ?? DEFAULT_PLATE_DESIGN_STATE,
    initialPlateMap: initialPlateMap ?? null,
    containerSchemaId: initialPlateDesignState?.plateSchema?.containerSchemaId ?? null,
    isCreatingPlateRecordFromDesign: initialPlateDesignState != null && plateMapType === PlateMapType.RECORD,
    dismissablePlateMapValidation,
  };
  const [{context: annotateFillPlateModalState}, dispatch] = useMachine(AnnotateFillPlateModalMachine, {
    input: initialState ?? defaultState,
  });

  const fetchTestOrders = shouldShowTestOrderDilutionInfo(procedureStudyDesignType, plateMapType);
  const {plateMap, isPending} = usePlateMapFromGrid(
    annotateFillPlateModalState.selectedGrid,
    fetchTestOrders
  );

  useEffect(() => {
    if (
      defaultState.initialPlateMap == null &&
      annotateFillPlateModalState.plateMapType === PlateMapType.RECORD &&
      annotateFillPlateModalState.plateDefinitionType === PlateDefinitionType.EXISTING &&
      plateMap?.id !== annotateFillPlateModalState.initialPlateMap?.id &&
      !isPending
    ) {
      dispatch({
        type: AnnotateFillPlateModalReducerActionType.UPDATE_INITIAL_PLATE_MAP,
        updatedPlateMapInfo:
          plateMap != null && annotateFillPlateModalState.selectedGrid != null
            ? {
                plateMap,
                containerSchemaId: annotateFillPlateModalState.selectedGrid.storableSchema.containerSchemaId!,
              }
            : null,
        isInProcedure,
        plateDataParameters,
      });
    }
  }, [
    annotateFillPlateModalState.initialPlateMap?.id,
    annotateFillPlateModalState.plateMapType,
    annotateFillPlateModalState.plateDefinitionType,
    annotateFillPlateModalState.selectedGrid,
    isInProcedure,
    dispatch,
    isPending,
    plateMap,
    plateDataParameters,
    defaultState.initialPlateMap,
  ]);

  return [annotateFillPlateModalState, dispatch];
}
